import type { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import type { ISegment, ISegmentFormData } from '@/Interfaces/Segments/Segment.interface';
import { MAX_SEGMENTS_QUERY_PARAMS_LENGTH } from '@/constants/axios';
import batchRequest from '@/helpers/batchRequest';
import axios, { AxiosRequestConfig } from 'axios';

const segmentsEndpoint = '/segments';
const segmentsCreativesEndpoint = `${segmentsEndpoint}/creatives`;
const segmentsWithIdEndpoint = (id: ISegment['id']) => `${segmentsEndpoint}/${id}`;
const archiveSegmentWithIdEndpoint = (id: ISegment['id']) => `${segmentsEndpoint}/setActive/${id}`;

//#region segments
interface ICreateSegmentPayload {
  segmentName: string;
  advertiserId: number;
  creativeIds?: ICreative['id'][];
}

interface IArchiveSegmentPayload {
  id: number;
  active: boolean;
}

const getPostRequestBody = (formData: ISegmentFormData): ICreateSegmentPayload => {
  const { name: segmentName, advertiser, creatives } = formData;
  const { value: advertiserId } = advertiser;
  const creativeIds = creatives.map(c => c.id);

  const requestBody: ICreateSegmentPayload = { segmentName, advertiserId, creativeIds };

  return requestBody;
};

const getPutRequestBody = (formData: ISegmentFormData): Partial<ICreateSegmentPayload> => {
  const { name: segmentName, creatives } = formData;

  const creativeIds = creatives.map(c => c.id);

  const requestBody: Partial<ICreateSegmentPayload> = { segmentName, creativeIds };

  return requestBody;
};

export const fetchSegments = async (
  advertiserIds: IAdvertiser['id'][],
  params: AxiosRequestConfig['params'],
  signal?: AbortSignal,
) => {
  if (!advertiserIds?.length) return [];

  const batchFetchSegments = batchRequest(async advertiserIdBatch => {
    const response = await axios.get(segmentsEndpoint, {
      params: {
        ...params,
        advertiserIds: advertiserIdBatch,
      },
      signal,
    });
    return response.data || [];
  }, MAX_SEGMENTS_QUERY_PARAMS_LENGTH);

  return batchFetchSegments(advertiserIds);
};

export const fetchSegment = async (segmentId: ISegment['id'], signal?: AbortSignal) => {
  const response = await axios.get(segmentsWithIdEndpoint(segmentId), {
    signal,
  });
  return response.data;
};

export const fetchCreatives = async (params: AxiosRequestConfig['params'], signal?: AbortSignal) => {
  const response = await axios.get<IPaginatedApiResponse<ICreative[]>>(segmentsCreativesEndpoint, {
    params,
    signal,
  });
  return response.data;
};

export const createSegment = (formData: ISegmentFormData) => {
  return axios.post<ISegment>(segmentsEndpoint, getPostRequestBody(formData));
};

export const updateSegment = (formData: ISegmentFormData) => {
  return axios.put(segmentsWithIdEndpoint(formData.id!), getPutRequestBody(formData));
};

export const archiveSegment = async (payload: IArchiveSegmentPayload) => {
  const { id, ...rest } = payload;
  return axios.put(archiveSegmentWithIdEndpoint(id), rest);
};
//#endregion
